import { PropsWithChildren, useState } from "react";

import {
  createBrowserRouter,
  RouterProvider
} from "react-router-dom";

import {
  QueryCache,
  QueryClient, QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Box, CssBaseline, GlobalStyles } from "@mui/material";
import * as Sentry from "@sentry/react";

import {
  SnackbarProvider, ThemeProvider,
  Typography, lightProdTheme, useSnackbar
} from '@initia-digital/component-library';

import { BasicLogger } from "@initia-digital/ts-utils";

import { ErrorPage } from './pages/errorPage';
import { Index } from './pages';
import { ClosedPage } from './pages/ClosedPage';
import { CONFIG } from './config/const';

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });

const QueryClientProviderWrapper = ({ children }: PropsWithChildren) => {
  // --------------------------------------------
  const { queueSnackbar } = useSnackbar();

  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({
        // Centralized error handling for query keys here 
       onError: (error, query) => {
        // --------------------------------------------
        const { queryKey } = query;
        logger.error(`Error with query`, query);
        logger.error(`Error`, error);

        // Capture in Sentry with context
        Sentry.withScope((scope) => {
          scope.setTag('queryKey', queryKey.join(':'));
          scope.setContext('query', {
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            state: query.state
          });
          scope.setLevel(error.message.includes('Unauthorized') ? 'warning' : 'error');
          Sentry.captureException(error);
        });

        queueSnackbar(`An unexpected error has occurred. Please try https://forms.gle/Ym5F4wXf9za6scrg9 instead`, 'error', 'Error', 10000);
       }
     })
  }));

  return (    
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}

const router = createBrowserRouter([
  {
    path: "/",
    element: CONFIG.IS_FORM_OPEN ? <Index /> : <ClosedPage />,
    errorElement: <ErrorPage />
  }
]);

function App() {
  // --------------------------------------------
  return (    
      <ThemeProvider theme={lightProdTheme}>
          <GlobalStyles styles={{ body: { margin: '0px' } }} />
          <CssBaseline />
          <SnackbarProvider anchorOrigin={{ vertical: "top", horizontal: "center" }}>
            <QueryClientProviderWrapper>
                <ReactQueryDevtools initialIsOpen={false} />
                <RouterProvider router={router} />
            </QueryClientProviderWrapper>
          </SnackbarProvider>
      </ThemeProvider>
  );
}

const SentryErrorBoundary = Sentry.withErrorBoundary(App, {
  fallback: () => {
    // --------------------------------------------
    return (
      <Box p="20px">
        <Typography fontSize="1rem" color="black" fontWeight={800}>
          Something went wrong
        </Typography>
        <Typography fontSize="0.8rem" color="black" fontWeight={300}>
          Please visit our fallback form at: https://forms.gle/Ym5F4wXf9za6scrg9
        </Typography>
      </Box>
    );
  },
  showDialog: true
});

export default SentryErrorBoundary;